import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface HighlightToggleProps {
  active: boolean;
  onToggle: () => void;
}

/**
 * `<HighlightToggle>` — single chip controlling whether properties inside
 * any visible amenity radius get the lit/larger styling on `/app/map`.
 *
 * UI-SPEC § 4.3:
 * - Active default; uses the Glass Coast `moss-500` accent (consistent
 *   with the in-radius property fill on the map).
 * - Inactive shows the EyeOff icon + muted bg.
 *
 * Accessibility: `role="switch"` + `aria-checked`; aria-label keeps the
 * verbose phrasing for screen readers.
 */
export function HighlightToggle({ active, onToggle }: HighlightToggleProps) {
  const Icon = active ? Eye : EyeOff;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={active}
      aria-label="Highlight properties within radius"
      onClick={onToggle}
      className={cn(
        'inline-flex h-8 px-3 rounded-full border items-center gap-1.5 text-sm font-semibold cursor-pointer',
        'transition-colors',
        'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
        active
          ? 'bg-moss-500/20 border-moss-500/60 text-foam-50'
          : 'bg-ocean-800/40 border-foam-50/10 text-foam-50/60',
      )}
    >
      <Icon className="w-4 h-4 shrink-0" aria-hidden />
      Highlight in radius
    </button>
  );
}
